import { FeatureVideoHelp } from "../../../components/ui/FeatureVideoHelp";
import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormPage } from "../../../components/layout/FormPage";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { AlertBanner, ContentCard, LoadingState, PageHeader, ui } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import { useGeoLocation } from "../hooks/useGeoLocation";
import { useLocalityDisplay } from "../hooks/useLocalityDisplay";
import { checkIn, checkOut, getReport, intermediateEntry, intermediateExit } from "../../../services/attendanceService";
import { formatTime } from "../utils/dateUtils";
import { LocationTroubleshootingModal } from "../components/LocationTroubleshootingModal";

export function MarkAttendancePage() {
  const navigate = useNavigate();
  const { latitude, longitude, error: geoError, isLoading: geoLoading, refetch } = useGeoLocation();
  const { locality: localityName, status: localityStatus, error: localityError, isLoading: localityLoading } = useLocalityDisplay(latitude, longitude);
  const [type, setType] = useState("Check In");
  const [locationInput, setLocationInput] = useState("");
  const [activities, setActivities] = useState("");
  const [tomorrowPlan, setTomorrowPlan] = useState("");
  const [reason, setReason] = useState("");
  const [todayStatus, setTodayStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);


  useEffect(() => {
    const empId = getAuthenticatedEmployeeId();
    if (!empId) {
      setLoadingStatus(false);
      return;
    }
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    getReport(today, today)
    .then((rows) => {
      const row = Array.isArray(rows) ? rows[0] : rows;
      if (!row) return;
      setTodayStatus({ attendanceId: row.attendanceId, checkIn: row.checkInTime, checkOut: row.checkOutTime });
    })
    .finally(() => setLoadingStatus(false));
  }, []);

  const attendanceId = todayStatus?.attendanceId || null;
  const alreadyCheckedIn = Boolean(todayStatus?.checkIn);
  const alreadyCheckedOut = Boolean(todayStatus?.checkOut);


  useEffect(() => {
    if (loadingStatus) return;
    if (alreadyCheckedOut) return;
    if (alreadyCheckedIn && type === "Check In") {
      setType("Check Out");
    }
    if (!alreadyCheckedIn && type !== "Check In") {
      setType("Check In");
    }
  }, [loadingStatus, alreadyCheckedIn, alreadyCheckedOut, type]);

  const hasLocation = latitude !== null && longitude !== null;
  const isValid =
  (type === "Check In" && locationInput.trim() && !alreadyCheckedIn) ||
  (type === "Check Out" && locationInput.trim() && activities.trim() && tomorrowPlan.trim() && attendanceId && alreadyCheckedIn && !alreadyCheckedOut) ||
  (type === "Intermediate Entry" && locationInput.trim() && attendanceId && alreadyCheckedIn && !alreadyCheckedOut) ||
  (type === "Intermediate Exit" && locationInput.trim() && reason.trim() && attendanceId && alreadyCheckedIn && !alreadyCheckedOut);
  const canSubmit = Boolean(isValid && hasLocation && localityStatus === "success" && !submitting);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (type === "Check In") await checkIn({ location: locationInput.trim(), latitude, longitude, geoLocation: localityName });

      if (type === "Check Out") await checkOut({ attendanceId: Number(attendanceId), location: locationInput.trim(), latitude, longitude, geoLocation: localityName, activitiesCompleted: activities.trim(), tomorrowPlan: tomorrowPlan.trim() });

      if (type === "Intermediate Entry") await intermediateEntry({ attendanceId: Number(attendanceId), location: locationInput.trim(), latitude, longitude, geoLocation: localityName });
      if (type === "Intermediate Exit") await intermediateExit({ attendanceId: Number(attendanceId), location: locationInput.trim(), latitude, longitude, geoLocation: localityName, reason: reason.trim() });
      navigate("/hrms-ess/attendance");
    } catch (err) {
      setSubmitError(err.message || "Failed to process attendance.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout>
          <div className="w-full">

              <PageHeader inlineControls className="w-full" back={<PageBackLink to="/hrms-ess/attendance" />} title={i18n.t("formActions.markAttendance")} action={<FeatureVideoHelp title="How to mark attendance" page="MarkAttendance" />} />

            <FormPage size="compact">
            <ContentCard bodyClassName="form-section">
            {loadingStatus ? <LoadingState message="Checking today's attendance record..." className="p-0 pb-3" /> : null}
            {type === "Check In" && alreadyCheckedIn ?
          <AlertBanner type="error" className="mb-3">
                <Trans i18nKey="attendance.alreadyCheckedInAt" /> {formatTime(todayStatus.checkIn)}.
              </AlertBanner> :
          null}
            {alreadyCheckedOut ?
          <AlertBanner type="error" className="mb-3">
                <Trans i18nKey="attendance.alreadyCheckedOutAt" /> {formatTime(todayStatus.checkOut)}<Trans i18nKey="dateTime.attendanceForTodayIsComplete" />
              </AlertBanner> :
          null}
            {!geoLoading && !geoError && hasLocation && localityLoading ?
          <AlertBanner type="info" className="mb-3">
                <Trans i18nKey="attendance.resolvingYourLocation" />
              </AlertBanner> :
          null}
            {!geoLoading && !geoError && hasLocation && localityStatus === "error" ?
          <AlertBanner type="error" className="mb-3">
                {localityError || "Location cannot be fetched"}{" "}
                <button type="button" className="text-accent cursor-pointer underline" onClick={refetch}>
                  <Trans i18nKey="attendance.retry" />
                </button>
              </AlertBanner> :
          null}
            {!geoLoading && !geoError && hasLocation && localityStatus === "success" ?
          <AlertBanner type="success" className="mb-3">
                <Trans i18nKey="attendance.locationFetchedSuccessfully" />
              </AlertBanner> :
          null}

            <div className="mb-3">
              <LocationTroubleshootingModal />
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="text-sm font-medium">
                  <Trans i18nKey="common.type" /> <span className="text-error">*</span>
                </label>
                <Select value={type} onValueChange={setType} disabled={alreadyCheckedOut}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl border-(--color-text-border) bg-white text-sm text-left focus:ring-accent">
                    <SelectValue />
                  </SelectTrigger>
                 <SelectContent className="min-w-(--radix-select-trigger-width)">
                   <SelectItem value="Check In" disabled={alreadyCheckedIn}><Trans i18nKey="attendance.checkIn" /></SelectItem>
                   <SelectItem value="Check Out" disabled={!alreadyCheckedIn || alreadyCheckedOut}><Trans i18nKey="attendance.checkOut" /></SelectItem>
                   {!alreadyCheckedOut ?
                  <SelectItem value="Intermediate Entry" disabled={!alreadyCheckedIn}><Trans i18nKey="attendance.intermediateEntry" /></SelectItem> :
                  null}
                   {!alreadyCheckedOut ?
                  <SelectItem value="Intermediate Exit" disabled={!alreadyCheckedIn}><Trans i18nKey="attendance.intermediateExit" /></SelectItem> :
                  null}
                 </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">
                  <Trans i18nKey="attendance.locationNameRemarks" /> <span className="text-error">*</span>
                </label>
                <input
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                placeholder={i18n.t("examples.eGHeadOffice")}
                disabled={alreadyCheckedOut}
                maxLength={40}
                className="mt-1 h-10 rounded-xl w-full border border-(--color-text-border) px-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed" />

              </div>
              {type === "Check Out" &&
            <>
                  <div>
                    <label className="text-sm font-medium">
                      <Trans i18nKey="attendance.activitiesCompleted" /> <span className="text-error">*</span>
                    </label>
                    <Textarea value={activities}  onChange={(e) => setActivities(e.target.value)} disabled={alreadyCheckedOut} className="mt-1 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      <Trans i18nKey="attendance.tomorrowPlan" /> <span className="text-error">*</span>
                    </label>
                    <Textarea value={tomorrowPlan} onChange={(e) => setTomorrowPlan(e.target.value)} disabled={alreadyCheckedOut} className="mt-1 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed" />
                  </div>
                </>
            }
              {type === "Intermediate Exit" &&
            <div>
                  <label className="text-sm font-medium">
                    <Trans i18nKey="attendance.activitiesCompleted" /> <span className="text-error">*</span>
                  </label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} disabled={alreadyCheckedOut} className="mt-1 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed" />
                </div>
            }
              {submitError ? <AlertBanner type="error">{submitError}</AlertBanner> : null}
              <div className="flex justify-end gap-2 pt-1 form-actions">
                <Button type="submit" disabled={!canSubmit} className={ui.primaryBtn}>
                  {submitting ? "Saving..." : "Save"}
                </Button>
                <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/hrms-ess/attendance")}
                className="h-11 rounded-xl px-5 cursor-pointer">
                  <Trans i18nKey="formActions.cancel" />
              </Button>
              </div>
            </form>
            </ContentCard>
          </FormPage>
          </div>
    </AppLayout>);

}
